import React, { useCallback, useMemo } from 'react';

import styled, { css } from 'styled-components';

import {
  SortDropdown as BaseSortDropdown,
  desktop,
  mobile,
  useBreakpoint,
  useProductBrowser,
} from '@techstyle/react-components';

import { usePageNum } from '../GridPageNumProvider/GridPageNumProvider';

const SortDropdown = styled(BaseSortDropdown)`
  align-items: baseline;
  ${BaseSortDropdown.DropdownList} {
    z-index: ${({ theme }) => theme.zIndex.productSortDropdown};
  }
`;

const ChevronIcon = styled(BaseSortDropdown.ChevronIcon)`
  color: ${({ theme }) => theme.colors.active};
  ${desktop`
    color: inherit;
    height: 10px;
    width: 10px;
    margin-top: 2px;
    margin-left: 4px;
  `}
`;

const desktopLabelStyle = css`
  font-size: 12px;
  font-weight: normal;
  letter-spacing: ${1.75 / 12}em;
  color: ${({ theme }) => theme.colors.subdued};
  text-transform: uppercase;
  margin-right: ${({ theme }) => theme.spacing.tiny}px;
`;

const mobileLabelStyle = css`
  font-size: 12px;
  font-weight: 500;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: ${1 / 12}em;
  color: ${({ theme }) => theme.colors.subdued};

  &[data-expanded='true'] {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const dropdownListStyle = css`
  ${desktop`
    font-size: 14px;
  `}

  border-top: 1px solid ${({ theme }) => theme.colors.gunmetal};
  border-right: 1px solid ${({ theme }) => theme.colors.gunmetal};
  border-left: 1px solid ${({ theme }) => theme.colors.gunmetal};
`;

const dropdownListItemControlStyle = css`
  width: 100vw;
  border: none;
  box-shadow: inset 0 -1px 0 0 ${props => props.theme.colors.lavender250};
  padding: 20px ${({ theme }) => theme.spacing.tiny}px;

  &[aria-current='true'] {
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.active};
  }

  ${desktop`
    width: auto;
    padding: 13px 16px;
  `}

  ${mobile`
    max-width: calc(100vw - ${({ theme }) => theme.spacing.medium}px);
  `}
`;

const dropdownListItemVariantStyle = css`
  width: 100vw;
  border: none;
  padding: 20px ${({ theme }) => theme.spacing.tiny}px;

  &[aria-current='true'] {
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.active};
  }

  ${desktop`
    width: auto;
    color: ${({ theme }) => theme.colors.black900};

    &:hover, &[aria-current='true'] {
      color: ${({ theme }) => theme.colors.white};
      background-color: ${({ theme }) => theme.colors.black};
    }
  `}
  ${({ theme }) => theme.paragraph.variants.paragraph4Uppercase.textStyles}
  border-bottom: 1px solid ${({ theme }) => theme.colors.gunmetal};
  ${desktop`
    padding: ${({ theme }) => theme.spacing.tiny}px ${({ theme }) =>
    theme.spacing.small}px;
  `}
`;

const activeSelectionControlStyle = css`
  color: ${({ theme }) => theme.colors.default};
  font-size: 14px;
  letter-spacing: ${0.3 / 14}em;
`;

const activeSelectionVariantStyle = css`
  color: ${({ theme }) => theme.colors.default};
  font-size: 14px;
  letter-spacing: ${0.3 / 14}em;

  ${desktop`
    color: inherit;

    &[aria-expanded='true'], &[data-expanded='true'] {
      color: ${({ theme }) => theme.colors.gray500};
    }
  `}

  ${({ theme }) => theme.paragraph.variants.paragraph4Uppercase.textStyles}
`;

const buttonControlStyle = css`
  color: ${({ theme }) => theme.colors.default};
  line-height: normal;

  &[aria-expanded='true'] {
    color: ${({ theme }) => theme.colors.white};
    background-color: ${({ theme }) => theme.colors.active};

    & ${ChevronIcon} {
      color: inherit;
    }

    ${desktop`
      border-radius: 4px 4px 0 0;
    `}
  }

  ${desktop`
    border-radius: 4px;
    height: auto;
    padding: ${({ theme }) => theme.spacing.tiny}px;
  `}
`;

const buttonVariantStyle = css`
  color: ${({ theme }) => theme.colors.default};
  line-height: normal;

  &[aria-expanded='true'] {
    color: ${({ theme }) => theme.colors.white};
    background-color: ${({ theme }) => theme.colors.active};

    & ${ChevronIcon} {
      color: inherit;
    }

    ${desktop`
      border-radius: 4px 4px 0 0;
    `}
  }

  ${desktop`
    color: inherit;
    border-radius: 4px;
    height: auto;
    padding: ${({ theme }) => theme.spacing.tiny}px 0;

    &[aria-expanded='true'] {
      color: ${({ theme }) => theme.colors.gray500};
      background-color: unset;
    }
  `}
`;

const buttonListWrapperControlStyle = css``;

const buttonListWrapperVariantStyle = css`
  ${desktop`
      color: ${({ theme }) => theme.colors.black900};

      &:hover {
        color: ${({ theme }) => theme.colors.gray500};
      }
    `}
`;

const ProductSortDropdown = () => {
  const { openOverlay, closeOverlay, setSort } = useProductBrowser();

  const { isDesktop } = useBreakpoint();
  const { setPageNum } = usePageNum();

  const handleChange = useCallback(
    option => {
      setSort(option);
      setPageNum(1);
    },
    [setSort, setPageNum]
  );

  const {
    buttonListWrapperStyle,
    buttonStyle,
    activeSelectionStyle,
    dropdownListItemStyle,
  } = useMemo(() => {
    const useVariant = isDesktop;
    return {
      buttonListWrapperStyle: useVariant
        ? buttonListWrapperVariantStyle
        : buttonListWrapperControlStyle,
      buttonStyle: useVariant ? buttonVariantStyle : buttonControlStyle,
      activeSelectionStyle: useVariant
        ? activeSelectionVariantStyle
        : activeSelectionControlStyle,
      dropdownListItemStyle: useVariant
        ? dropdownListItemVariantStyle
        : dropdownListItemControlStyle,
    };
  }, [isDesktop]);

  return (
    <SortDropdown
      buttonListWrapperStyle={buttonListWrapperStyle}
      buttonStyle={buttonStyle}
      desktopLabelStyle={desktopLabelStyle}
      dropdownListStyle={dropdownListStyle}
      activeSelectionStyle={activeSelectionStyle}
      dropdownListItemStyle={dropdownListItemStyle}
      mobileLabelStyle={mobileLabelStyle}
      onChange={handleChange}
      onOpen={openOverlay}
      onClose={closeOverlay}
      showMobileComma={false}
      chevronIcon={ChevronIcon}
    />
  );
};

export default ProductSortDropdown;
